import { NEUTRAL_TONE, type Tone } from "@kensio/pinyinjs";

/**
 * Tone colouring, on the screen and on the clipboard.
 *
 * The site has coloured pinyin by tone since long before this tool existed —
 * `span.tone-0` to `span.tone-4` in `components/hanzi.scss`, painted from the
 * `--cb-tone-*` custom properties. The tool reuses those classes rather than
 * inventing its own, so that a converted sentence here and an example sentence
 * in a grammar article are the same colours, and a change to the palette
 * reaches both.
 *
 * The one place that cannot use a class is the clipboard, which is what the
 * `-ink` properties below are for.
 */

/**
 * The class that colours a syllable of a given tone.
 *
 * The package numbers the neutral tone 5 and this site numbers it 0, which is
 * the whole of the translation. A syllable with no tone written at all — which
 * is not the same as a neutral one — gets no class and stays the colour of the
 * text around it.
 */
export function toneClass(tone: Tone | undefined): string | undefined {
  if (tone === undefined) {
    return undefined;
  }

  return tone === NEUTRAL_TONE ? "tone-0" : `tone-${String(tone)}`;
}

/**
 * The custom property holding the colour this tone is written in on paper.
 *
 * **Why a second set of properties rather than `--cb-tone-1`.** Pasting is the
 * point of the copy button, and what gets pasted has to carry literal colours:
 * a class means nothing in Word, and `var(--cb-tone-1)` means nothing outside
 * this stylesheet. So the colours have to be read out of the page and written
 * into the markup as hex.
 *
 * Read out of `--cb-tone-1` they would be *the visitor's* colours, and a
 * visitor reading the site in dark mode would paste the dark scheme's pale
 * pinyin onto a white page, where the third tone's `#4fc46a` is close to
 * invisible. `--cb-tone-1-ink` is the light scheme's value under both schemes,
 * because a document is paper-coloured whatever the screen was.
 */
export function toneInkProperty(tone: Tone | undefined): string | undefined {
  const className = toneClass(tone);

  return className === undefined ? undefined : `--cb-${className}-ink`;
}

/**
 * The colours to paste with, read from the stylesheet.
 *
 * Resolved against a real element rather than hard-coded, so that the palette
 * in `config/theme.scss` stays the only place the site's tone colours are
 * written down. A property the stylesheet does not define resolves to the
 * empty string, which is returned as undefined so a caller writes no colour at
 * all rather than `color: `.
 */
export function inkColour(
  tone: Tone | undefined,
  element: Element,
): string | undefined {
  const property = toneInkProperty(tone);
  if (property === undefined) {
    return undefined;
  }

  const value = getComputedStyle(element).getPropertyValue(property).trim();

  return value === "" ? undefined : value;
}
